import { useCallback } from 'react';
import type { TargetType } from '@atlassiansox/cross-flow-base-types';
import { useCrossFlowEditionParity } from '@jira/cross-flow-edition-parity/src/services/index.tsx';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { useIntl } from '@atlassian/jira-intl';
import type { CreateProjectResponse } from '@atlassian/jira-project-create-common/src/services/create-project/types.tsx';
import type { ApplicationEdition } from '@atlassian/jira-shared-types/src/edition.tsx';
import { useSubmitProjectCreate } from '../../common/services/submit-project-create/index.tsx';
import { getTemplateTmpKeyFromKey } from '../get-template-tmp-key-from-key/index.tsx';
import type { TemplateKey } from '../types.tsx';
import { useLegalNotice } from '../../common/ui/content/legal-notice/useLegalNotice.tsx';

export interface TemplateContent {
	templateTitle: {
		defaultMessage: string;
		description?: string;
		id?: string;
	};
}

export interface UseProjectCreationHandlerProps {
	templateKey: TemplateKey;
	templateContent: TemplateContent;
	targetProduct: TargetType;
	hasPaymentDetails: boolean;
	onSuccess: (
		response: CreateProjectResponse,
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		analyticsEvent: UIAnalyticsEvent,
	) => void;
	onFail: (error: Error) => void;
	sourceComponent: string;
}

export interface UseProjectCreationHandlerReturn {
	handleProjectCreation: (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		analyticsEvent: UIAnalyticsEvent,
	) => Promise<void>;
	isCreating: boolean;
	isUpgrading: boolean;
	isDisabled: boolean;
	targetEdition: ApplicationEdition | null;
	shouldUpgrade: boolean;
}

export const useProjectCreationHandler = ({
	templateKey,
	templateContent,
	targetProduct,
	hasPaymentDetails,
	onSuccess,
	onFail,
}: UseProjectCreationHandlerProps): UseProjectCreationHandlerReturn => {
	const { formatMessage, locale } = useIntl();

	const {
		upgradeCCPEntitlement,
		entitlementUpgrading,
		loadingUpgradeData,
		targetEdition,
		shouldUpgrade,
	} = useCrossFlowEditionParity({
		sourceProduct: 'jira-software.ondemand',
		targetProduct,
	});

	const tosPrivacyTextMessage = useLegalNotice({
		edition: targetEdition,
		hasPaymentDetails,
	});

	const [submitProjectCreate, isProjectCreateLoading, _hasProjectCreateError] =
		useSubmitProjectCreate(onSuccess, onFail);

	const handleProjectCreation = useCallback(
		async (
			e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
			analyticsEvent: UIAnalyticsEvent,
		) => {
			if (!entitlementUpgrading && !loadingUpgradeData) {
				await upgradeCCPEntitlement({
					tosPrivacyText: tosPrivacyTextMessage?.tosPrivacyTextForConsent,
					locale,
				});
			}

			await submitProjectCreate(
				{
					payload: {
						templateKey: getTemplateTmpKeyFromKey(templateKey),
						name: formatMessage(templateContent.templateTitle),
					},
					analyticsAttributes: {},
				},
				e,
				analyticsEvent,
			);
		},
		[
			entitlementUpgrading,
			formatMessage,
			loadingUpgradeData,
			locale,
			submitProjectCreate,
			templateContent.templateTitle,
			templateKey,
			tosPrivacyTextMessage,
			upgradeCCPEntitlement,
		],
	);

	return {
		handleProjectCreation,
		isCreating: isProjectCreateLoading,
		isUpgrading: entitlementUpgrading,
		isDisabled: loadingUpgradeData,
		targetEdition,
		shouldUpgrade,
	};
};
